import { getRuntimeEnvironment } from "../local/runtimeEnvironment";
import { isBranchScheduleOpen } from "../local/branchOperatingHours";
import { loadRememberedPublicDeliveryOrders } from "./publicDeliveryService";
import {
  getLocalWeRiderAssignment,
  getLocalWeRiderTracking,
} from "./weRiderService";
import { supabase } from "./client";
import {
  isSupabaseConfigured,
  supabaseAnonKey,
  supabaseUrl,
} from "./config";

const PUBLIC_WE_FUNCTION = "public-we-pedidos";
const LOCAL_SETTINGS_PREFIX = "mozzoapp:we-pedidos-settings:";
const LOCAL_PREVIEW_RESTAURANTS_KEY = "mozzoapp:we-pedidos-preview-restaurants";
const LOCAL_CUSTOMER_ORDERS_PREFIX = "mozzoapp:we-pedidos-customer-orders:v3:";
export const WE_PEDIDOS_REVIEW_DELAY_MS = 30 * 60 * 1000;
export const WE_PEDIDOS_RECENT_ORDER_MS = 2 * 24 * 60 * 60 * 1000;
export const WE_PEDIDOS_PREVIEW_UPDATED_EVENT =
  "mozzoapp:we-pedidos-preview-updated";
export const WE_PEDIDOS_ORDERS_UPDATED_EVENT =
  "mozzoapp:we-pedidos-orders-updated";

const isDevelopment = () => getRuntimeEnvironment() === "development";

const FINISHED_CUSTOMER_ORDER_STATUSES = new Set([
  "cancelled",
  "closed",
  "completed",
  "delivered",
  "refunded",
  "rejected",
]);

export const partitionWeCustomerOrders = (
  orders,
  now = Date.now()
) => {
  const visibleOrders = [];
  const oldOrders = [];

  (Array.isArray(orders) ? orders : []).forEach((order) => {
    const createdAt = new Date(order?.createdAt || 0).getTime();
    const isFinished = [order?.trackingStatus, order?.status]
      .map((status) => String(status || "").toLowerCase())
      .some((status) => FINISHED_CUSTOMER_ORDER_STATUSES.has(status));
    const isOlderThanTwoDays =
      Number.isFinite(createdAt) &&
      createdAt > 0 &&
      Number(now) - createdAt > WE_PEDIDOS_RECENT_ORDER_MS;

    if (isFinished && isOlderThanTwoDays) {
      oldOrders.push(order);
    } else {
      visibleOrders.push(order);
    }
  });

  return { visibleOrders, oldOrders };
};

export const resolveWePedidosNavigationUrl = (value) => {
  if (!value || typeof window === "undefined") return value || "";

  try {
    const targetUrl = new URL(value, window.location.origin);
    const isMozzoHost = /(^|\.)mozzoapp\.com$/i.test(targetUrl.hostname);

    if (targetUrl.origin === window.location.origin || isMozzoHost) {
      return `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`;
    }

    return targetUrl.toString();
  } catch {
    return value;
  }
};

const withCurrentOriginTracking = (order) =>
  order?.trackingUrl
    ? {
        ...order,
        trackingUrl: resolveWePedidosNavigationUrl(order.trackingUrl),
      }
    : order;

const DELIVERY_STATUS_META = Object.freeze({
  waiting_restaurant: {
    status: null,
    label: "Esperando al restaurante",
    tone: "pending",
  },
  searching_driver: {
    status: null,
    label: "Buscando repartidor",
    tone: "active",
  },
  driver_assigned: {
    status: "assigned_to_driver",
    label: "Repartidor asignado",
    tone: "active",
  },
  arriving_pickup: {
    status: "assigned_to_driver",
    label: "El Rider va al restaurante",
    tone: "active",
  },
  arrived_pickup: {
    status: "assigned_to_driver",
    label: "El Rider llegó al restaurante",
    tone: "active",
  },
  ready_for_pickup: {
    status: "assigned_to_driver",
    label: "Listo para retirar",
    tone: "active",
  },
  picked_up: {
    status: "out_for_delivery",
    label: "Pedido retirado",
    tone: "active",
  },
  in_transit: {
    status: "out_for_delivery",
    label: "En camino",
    tone: "active",
  },
  delivered: {
    status: "delivered",
    label: "Entregado",
    tone: "complete",
  },
  cancelled: {
    status: "cancelled",
    label: "Cancelado",
    tone: "cancelled",
  },
});

const applyDeliveryTrackingState = (order) => {
  if (!order?.id) return order;

  const localAssignment = getLocalWeRiderAssignment(order.id);
  const trackingStatus =
    localAssignment?.status || order.trackingStatus || order.tracking_status;
  const meta = DELIVERY_STATUS_META[trackingStatus];
  if (!meta) return withCurrentOriginTracking(order);

  const trackingToken =
    localAssignment?.tracking_token || order.trackingToken || order.tracking_token;
  const trackingUrl =
    order.trackingUrl ||
    (trackingToken
      ? `/we-pedidos/seguimiento/${encodeURIComponent(trackingToken)}`
      : "");

  return withCurrentOriginTracking({
    ...order,
    status: meta.status || order.status,
    statusLabel: meta.label,
    statusTone: meta.tone,
    trackingStatus,
    trackingToken: trackingToken || order.trackingToken || "",
    trackingUrl,
    deliveryVerificationCode:
      ["picked_up", "in_transit"].includes(trackingStatus)
        ? localAssignment?.delivery_verification_code ||
          order.deliveryVerificationCode ||
          null
        : null,
  });
};

const applyDeliveryTrackingStates = (orders) =>
  (Array.isArray(orders) ? orders : []).map(applyDeliveryTrackingState);

const getLocalCustomerOrdersKey = (accountKey) =>
  `${LOCAL_CUSTOMER_ORDERS_PREFIX}${encodeURIComponent(
    String(accountKey || "").trim().toLowerCase()
  )}`;

const readLocalCustomerOrders = (accountKey) => {
  if (typeof window === "undefined" || !accountKey) return [];
  try {
    const parsed = JSON.parse(
      window.localStorage.getItem(getLocalCustomerOrdersKey(accountKey)) || "[]"
    );
    return Array.isArray(parsed) ? parsed.filter((order) => order?.id) : [];
  } catch {
    return [];
  }
};

const mergeCustomerOrders = (cachedOrders = [], remoteOrders = []) => {
  const ordersById = new Map();
  [...cachedOrders, ...remoteOrders].forEach((order) => {
    if (order?.id) {
      ordersById.set(String(order.id), withCurrentOriginTracking(order));
    }
  });
  return [...ordersById.values()]
    .sort(
      (left, right) =>
        new Date(right?.createdAt || 0).getTime() -
        new Date(left?.createdAt || 0).getTime()
    )
    .slice(0, 50);
};

const writeLocalCustomerOrders = (
  accountKey,
  orders,
  { notify = true } = {}
) => {
  if (typeof window === "undefined" || !accountKey) return;
  window.localStorage.setItem(
    getLocalCustomerOrdersKey(accountKey),
    JSON.stringify(Array.isArray(orders) ? orders.slice(0, 50) : [])
  );
  if (notify) {
    window.dispatchEvent(new CustomEvent(WE_PEDIDOS_ORDERS_UPDATED_EVENT));
  }
};

export const rememberWeCustomerOrder = ({ accountKey, order }) => {
  if (!accountKey || !order?.id) return;
  writeLocalCustomerOrders(
    accountKey,
    mergeCustomerOrders(readLocalCustomerOrders(accountKey), [order])
  );
};

export const getWeRestaurantRatingEligibility = (
  order,
  now = Date.now()
) => {
  const createdAtMs = new Date(order?.createdAt || 0).getTime();
  const reviewAvailableAt = Number.isFinite(createdAtMs)
    ? new Date(createdAtMs + WE_PEDIDOS_REVIEW_DELAY_MS).toISOString()
    : "";
  const delivered =
    order?.status === "delivered" || order?.trackingStatus === "delivered";
  const alreadyReviewed = Number(order?.review?.rating) >= 1;
  const delayElapsed =
    Boolean(reviewAvailableAt) &&
    new Date(reviewAvailableAt).getTime() <= Number(now || Date.now());

  return {
    delivered,
    alreadyReviewed,
    reviewAvailableAt,
    canReview: delivered && delayElapsed && !alreadyReviewed,
  };
};

const normalizeResponseError = async (error, fallbackMessage) => {
  if (error?.context && typeof error.context.json === "function") {
    try {
      const payload = await error.context.json();
      if (payload?.error) return String(payload.error);
    } catch {
      // Supabase may return a gateway response while an Edge Function starts.
    }
  }

  return error?.message || fallbackMessage;
};

const isMissingWeSchema = (error) => {
  const details = `${error?.message || ""} ${error?.details || ""}`.toLowerCase();
  return (
    error?.code === "42P01" ||
    error?.code === "PGRST205" ||
    details.includes("we_branch_settings") ||
    details.includes("public-we-pedidos")
  );
};

const readLocalPreviewRestaurants = () => {
  if (typeof window === "undefined") return [];

  try {
    const parsed = JSON.parse(
      window.localStorage.getItem(LOCAL_PREVIEW_RESTAURANTS_KEY) || "[]"
    );
    return Array.isArray(parsed) ? parsed.filter((item) => item?.id) : [];
  } catch {
    return [];
  }
};

const writeLocalPreviewRestaurants = (restaurants) => {
  if (typeof window === "undefined") return;
  const serializedRestaurants = JSON.stringify(restaurants);

  if (
    window.localStorage.getItem(LOCAL_PREVIEW_RESTAURANTS_KEY) ===
    serializedRestaurants
  ) {
    return;
  }

  window.localStorage.setItem(
    LOCAL_PREVIEW_RESTAURANTS_KEY,
    serializedRestaurants
  );
  window.dispatchEvent(new CustomEvent(WE_PEDIDOS_PREVIEW_UPDATED_EVENT));
};

export const syncWeLocalPreview = ({ settings, restaurant }) => {
  if (!isDevelopment() || !settings?.branch_id) return;

  const current = readLocalPreviewRestaurants().filter(
    (item) => item.id !== settings.branch_id
  );
  const next =
    settings.enabled === true && restaurant?.city
      ? [
          {
            ...restaurant,
            id: settings.branch_id,
            acceptingOrders: settings.accepting_orders !== false,
            priceLevel: Number(settings.price_level || restaurant.priceLevel || 2),
            serviceNotice: settings.service_notice || "",
            isOpen: Array.isArray(restaurant.horarioDetallado)
              ? isBranchScheduleOpen(restaurant.horarioDetallado, {
                  timezone: restaurant.timezone,
                })
              : restaurant.isOpen !== false,
            localPublished: true,
          },
          ...current,
        ]
      : current;

  writeLocalPreviewRestaurants(next);
};

const mergeRestaurantsById = (...groups) => {
  const merged = new Map();
  groups.flat().forEach((restaurant) => {
    if (restaurant?.id) merged.set(restaurant.id, restaurant);
  });
  return [...merged.values()];
};

const buildLocalDiscovery = ({ city = "Corrientes", latitude, longitude } = {}) => {
  const localPublished = readLocalPreviewRestaurants();

  return {
    ok: true,
    source: localPublished.length ? "local_published" : "local_empty",
    location: {
      city: city || "Corrientes",
      state: "Corrientes",
      label: city || "Corrientes",
      latitude: Number.isFinite(Number(latitude)) ? Number(latitude) : -27.4692,
      longitude: Number.isFinite(Number(longitude)) ? Number(longitude) : -58.8306,
    },
    restaurants: localPublished,
  };
};

const useCurrentOriginForMenu = (restaurant) => {
  if (typeof window === "undefined" || !restaurant?.menuUrl) {
    return restaurant;
  }

  return {
    ...restaurant,
    menuUrl: resolveWePedidosNavigationUrl(restaurant.menuUrl),
  };
};

const invokePublicWe = async (
  body,
  fallbackMessage,
  { accessToken = "", allowDevelopmentFallback = true } = {}
) => {
  if (!isSupabaseConfigured || !supabase) {
    if (isDevelopment()) return null;
    throw new Error("We! pedidos no está configurado en este entorno.");
  }

  let timeoutId = null;
  try {
    const endpoint = isDevelopment()
      ? "/api/we-pedidos-public"
      : `${supabaseUrl}/functions/v1/${PUBLIC_WE_FUNCTION}`;
    const controller = new AbortController();
    timeoutId = window.setTimeout(() => controller.abort(), 12000);
    const response = await fetch(
      endpoint,
      {
        method: "POST",
        headers: {
          apikey: supabaseAnonKey,
          Authorization: `Bearer ${accessToken || supabaseAnonKey}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
        signal: controller.signal,
        cache: "no-store",
      }
    );
    const data = await response.json().catch(() => null);

    if (!response.ok || !data || data.ok === false) {
      throw new Error(data?.error || data?.message || fallbackMessage);
    }

    return data;
  } catch (error) {
    if (isDevelopment() && allowDevelopmentFallback) return null;
    throw new Error(await normalizeResponseError(error, fallbackMessage));
  } finally {
    if (timeoutId !== null) window.clearTimeout(timeoutId);
  }
};

export const discoverWeRestaurants = async ({
  city = "",
  address = "",
  latitude = null,
  longitude = null,
  search = "",
} = {}) => {
  const response = await invokePublicWe(
    {
      action: "discover",
      city,
      address,
      latitude,
      longitude,
      search,
    },
    "No pudimos buscar restaurantes en We! pedidos."
  );

  if (response && !isDevelopment()) {
    return response;
  }

  if (response) {
    const liveRestaurants = (response.restaurants || []).map(
      useCurrentOriginForMenu
    );
    const localPublished = readLocalPreviewRestaurants();
    if (localPublished.length) {
      return {
        ...response,
        source: liveRestaurants.length ? response.source || "live" : "local_published",
        restaurants: mergeRestaurantsById(
          liveRestaurants,
          localPublished
        ),
      };
    }
    if (liveRestaurants.length) {
      return { ...response, restaurants: liveRestaurants };
    }
  }

  return buildLocalDiscovery({ city, latitude, longitude });
};

export const loadWeDeliveryTracking = async (
  trackingToken,
  { accessToken = "" } = {}
) => {
  const localTracking = getLocalWeRiderTracking(trackingToken);
  if (localTracking) return localTracking;

  if (isDevelopment() && String(trackingToken || "").toUpperCase() === "DEMO") {
    return {
      ok: true,
      tracking: {
        displayNumber: "DEL-DEMO",
        restaurantName: "Mozzo Centro",
        status: "in_transit",
        statusLabel: "En camino",
        customerMessage: "Tu pedido salió del restaurante.",
        estimatedArrivalAt: new Date(Date.now() + 18 * 60 * 1000).toISOString(),
        driver: { firstName: "Martín", vehicleType: "Moto" },
        events: [
          { status: "waiting_restaurant", label: "Pedido recibido", createdAt: new Date(Date.now() - 34 * 60 * 1000).toISOString() },
          { status: "searching_driver", label: "Buscando repartidor", createdAt: new Date(Date.now() - 21 * 60 * 1000).toISOString() },
          { status: "picked_up", label: "Retirado del local", createdAt: new Date(Date.now() - 8 * 60 * 1000).toISOString() },
        ],
      },
    };
  }

  return invokePublicWe(
    { action: "track", trackingToken },
    "No pudimos consultar el seguimiento del pedido.",
    { accessToken }
  );
};

export const loadWeCustomerOrders = async ({ accessToken, accountKey } = {}) => {
  const cachedOrders = readLocalCustomerOrders(accountKey);
  if (!accessToken) {
    if (isDevelopment() && accountKey) {
      const rememberedOrders = await loadRememberedPublicDeliveryOrders();
      const orders = applyDeliveryTrackingStates(
        mergeCustomerOrders(cachedOrders, rememberedOrders)
      );
      writeLocalCustomerOrders(accountKey, orders, { notify: false });
      return { ok: true, orders, source: "local_account" };
    }
    throw new Error("Inicia sesion en We! pedidos para ver tus pedidos.");
  }

  try {
    const response = await invokePublicWe(
      { action: "my_orders" },
      "No pudimos recuperar tus pedidos.",
      { accessToken, allowDevelopmentFallback: false }
    );
    const orders = applyDeliveryTrackingStates(
      mergeCustomerOrders([], response?.orders || [])
    );
    if (accountKey) {
      writeLocalCustomerOrders(accountKey, orders, { notify: false });
    }
    return { ...response, orders };
  } catch (error) {
    if (cachedOrders.length) {
      return {
        ok: true,
        orders: applyDeliveryTrackingStates(cachedOrders),
        source: "local_cache",
        warning: "Mostramos la ultima informacion guardada en este dispositivo.",
      };
    }
    throw error;
  }
};

export const requestWeCustomerOrderChange = async ({
  accessToken,
  accountKey,
  orderId,
  requestType,
  message,
} = {}) => {
  const normalizedOrderId = String(orderId || "").trim();
  const normalizedType = String(requestType || "").trim().toLowerCase();
  const normalizedMessage = String(message || "").trim().slice(0, 1000);
  if (!normalizedOrderId) throw new Error("No encontramos el pedido.");
  if (!["modification", "cancellation"].includes(normalizedType)) {
    throw new Error("Elegi si necesitas modificar o cancelar el pedido.");
  }
  if (normalizedMessage.length < 5) {
    throw new Error("Explica brevemente que necesitas cambiar o por que deseas cancelar.");
  }

  if (accessToken) {
    return invokePublicWe(
      {
        action: "request_order_change",
        orderId: normalizedOrderId,
        requestType: normalizedType,
        message: normalizedMessage,
      },
      "No pudimos enviar la solicitud al restaurante.",
      { accessToken }
    );
  }

  if (!isDevelopment() || !accountKey) {
    throw new Error("Inicia sesion en We! pedidos para enviar la solicitud.");
  }

  const orders = readLocalCustomerOrders(accountKey);
  const order = orders.find((item) => String(item?.id) === normalizedOrderId);
  if (!order) throw new Error("No encontramos el pedido en tu cuenta.");
  const customerRequest = {
    type: normalizedType,
    message: normalizedMessage,
    status: "pending",
    createdAt: new Date().toISOString(),
    resolvedAt: null,
  };
  writeLocalCustomerOrders(
    accountKey,
    orders.map((item) =>
      String(item?.id) === normalizedOrderId
        ? { ...item, customerRequest }
        : item
    )
  );
  return { ok: true, customerRequest, source: "local_account" };
};

export const submitWeRestaurantRating = async ({
  accessToken,
  accountKey,
  orderId,
  rating,
} = {}) => {
  const normalizedOrderId = String(orderId || "").trim();
  const normalizedRating = Number(rating);
  if (!normalizedOrderId) throw new Error("No encontramos el pedido a calificar.");
  if (!Number.isInteger(normalizedRating) || normalizedRating < 1 || normalizedRating > 5) {
    throw new Error("La calificacion debe ser de 1 a 5 estrellas.");
  }

  if (accessToken) {
    const response = await invokePublicWe(
      {
        action: "submit_review",
        orderId: normalizedOrderId,
        rating: normalizedRating,
      },
      "No pudimos guardar tu calificacion.",
      { accessToken }
    );
    if (response) return response;
  }

  if (!isDevelopment() || !accountKey) {
    throw new Error("Inicia sesion en We! pedidos para calificar.");
  }

  const orders = readLocalCustomerOrders(accountKey);
  const order = orders.find((item) => String(item?.id) === normalizedOrderId);
  if (!order) throw new Error("No encontramos el pedido en tu cuenta.");
  const eligibility = getWeRestaurantRatingEligibility(order);
  if (eligibility.alreadyReviewed) {
    return { ok: true, review: order.review, alreadySubmitted: true };
  }
  if (!eligibility.delivered) {
    throw new Error("Podras calificar cuando el pedido figure como entregado.");
  }
  if (!eligibility.canReview) {
    throw new Error("Podras calificar 30 minutos despues de realizar el pedido.");
  }

  const review = {
    rating: normalizedRating,
    createdAt: new Date().toISOString(),
  };
  writeLocalCustomerOrders(
    accountKey,
    orders.map((item) =>
      String(item?.id) === normalizedOrderId ? { ...item, review } : item
    )
  );
  return { ok: true, review, source: "local_account" };
};

export const submitWeProductRating = async ({
  accessToken,
  accountKey,
  orderId,
  orderItemId,
  rating,
} = {}) => {
  const normalizedOrderId = String(orderId || "").trim();
  const normalizedOrderItemId = String(orderItemId || "").trim();
  const normalizedRating = Number(rating);
  if (!normalizedOrderId || !normalizedOrderItemId) {
    throw new Error("No encontramos el plato a calificar.");
  }
  if (!Number.isInteger(normalizedRating) || normalizedRating < 1 || normalizedRating > 5) {
    throw new Error("La calificacion debe ser de 1 a 5 estrellas.");
  }

  if (accessToken) {
    const response = await invokePublicWe(
      {
        action: "submit_product_review",
        orderId: normalizedOrderId,
        orderItemId: normalizedOrderItemId,
        rating: normalizedRating,
      },
      "No pudimos guardar la calificacion del plato.",
      { accessToken }
    );
    if (response) return response;
  }

  if (!isDevelopment() || !accountKey) {
    throw new Error("Inicia sesion en We! pedidos para calificar.");
  }

  const orders = readLocalCustomerOrders(accountKey);
  const order = orders.find((item) => String(item?.id) === normalizedOrderId);
  if (!order) throw new Error("No encontramos el pedido en tu cuenta.");
  const eligibility = getWeRestaurantRatingEligibility(order);
  if (!eligibility.delivered) {
    throw new Error("Podras calificar cuando el pedido figure como entregado.");
  }
  if (new Date(eligibility.reviewAvailableAt || 0).getTime() > Date.now()) {
    throw new Error("Podras calificar 30 minutos despues de realizar el pedido.");
  }

  const orderItem = (order.items || []).find(
    (item) => String(item?.id) === normalizedOrderItemId
  );
  if (!orderItem) throw new Error("Ese plato no pertenece al pedido.");
  if (Number(orderItem?.review?.rating) >= 1) {
    return {
      ok: true,
      review: orderItem.review,
      alreadySubmitted: true,
    };
  }

  const review = {
    orderItemId: normalizedOrderItemId,
    productId: orderItem.productId || null,
    rating: normalizedRating,
    createdAt: new Date().toISOString(),
  };
  writeLocalCustomerOrders(
    accountKey,
    orders.map((item) =>
      String(item?.id) === normalizedOrderId
        ? {
            ...item,
            items: (item.items || []).map((productItem) =>
              String(productItem?.id) === normalizedOrderItemId
                ? { ...productItem, review }
                : productItem
            ),
          }
        : item
    )
  );
  return { ok: true, review, source: "local_account" };
};

const readLocalBranchSettings = (branchId) => {
  if (typeof window === "undefined") return null;

  try {
    const parsed = JSON.parse(
      window.localStorage.getItem(`${LOCAL_SETTINGS_PREFIX}${branchId}`) || "null"
    );
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
};

const writeLocalBranchSettings = (branchId, value) => {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(
    `${LOCAL_SETTINGS_PREFIX}${branchId}`,
    JSON.stringify(value)
  );
};

export const loadWeBranchSettings = async (branchId) => {
  if (!branchId) throw new Error("Selecciona una sucursal para configurar We! pedidos.");

  if (!isSupabaseConfigured || !supabase) {
    return (
      readLocalBranchSettings(branchId) || {
        branch_id: branchId,
        enabled: false,
        accepting_orders: true,
        price_level: 2,
      }
    );
  }

  const { data, error } = await supabase
    .from("we_branch_settings")
    .select("branch_id, enabled, accepting_orders, price_level, service_notice, updated_at")
    .eq("branch_id", branchId)
    .maybeSingle();

  if (error) {
    if (isDevelopment() && isMissingWeSchema(error)) {
      return (
        readLocalBranchSettings(branchId) || {
          branch_id: branchId,
          enabled: false,
          accepting_orders: true,
          price_level: 2,
          localPreview: true,
        }
      );
    }
    throw new Error(error.message || "No pudimos leer la configuración de We! pedidos.");
  }

  return (
    data || {
      branch_id: branchId,
      enabled: false,
      accepting_orders: true,
      price_level: 2,
    }
  );
};

export const saveWeBranchSettings = async ({
  branchId,
  enabled,
  acceptingOrders,
  priceLevel,
  serviceNotice,
}) => {
  const payload = {
    branch_id: branchId,
    enabled: enabled === true,
    accepting_orders: acceptingOrders !== false,
    price_level: Math.max(1, Math.min(4, Number(priceLevel || 2))),
    service_notice: String(serviceNotice || "").trim() || null,
    updated_at: new Date().toISOString(),
  };

  if (!isSupabaseConfigured || !supabase) {
    writeLocalBranchSettings(branchId, { ...payload, localPreview: true });
    return { ...payload, localPreview: true };
  }

  if (payload.enabled) {
    const { error: deliverySettingsError } = await supabase
      .from("branch_settings")
      .upsert({
        branch_id: branchId,
        delivery_enabled: true,
        operational_setup_completed: true,
      });

    if (deliverySettingsError) {
      throw new Error(
        deliverySettingsError.message ||
          "Para publicar en We! pedidos primero necesitamos habilitar Delivery."
      );
    }
  }

  const { data, error } = await supabase
    .from("we_branch_settings")
    .upsert(payload, { onConflict: "branch_id" })
    .select("branch_id, enabled, accepting_orders, price_level, service_notice, updated_at")
    .single();

  if (error) {
    if (isDevelopment() && isMissingWeSchema(error)) {
      writeLocalBranchSettings(branchId, { ...payload, localPreview: true });
      return { ...payload, localPreview: true };
    }
    throw new Error(error.message || "No pudimos guardar la configuración de We! pedidos.");
  }

  return data;
};
